import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import {t} from "../../Translations";
import {propertiesAreSet} from "../../utils";

import usageConsumptionPropTypes from "../../propTypes/usageConsumption";
import vehicleDetailPropTypes from "../../propTypes/vehicleDetail";

import Button from "../widgets/Button";
import StepTitle from "../widgets/StepTitle";
import NavResponsive from "../widgets/NavResponsive";
import ScrollOnMount from "../ScrollOnMount";

import Step2_1 from "../steps/Step2_1";
import Step2_2 from "../steps/Step2_2";
import Step2_3 from "../steps/Step2_3";
import Step2_4 from "../steps/Step2_4";
import UsageConsumptionResult from "../steps/UsageConsumptionResult";

/**
 * Step 2 component. Encapsulate all second step's components and manages step 2 data.
 */
class Step2 extends React.Component {

    static propTypes = {
        // Contain steps data
        data: PropTypes.object,

        // Data returned by the usage consumption calculation API
        usageConsumptionData: usageConsumptionPropTypes,

        // The user chosen vehicle detail, in human reading format
        vehicleDetail: vehicleDetailPropTypes,

        // Whether the consumption is calculating or not
        calculatingConsumption: PropTypes.bool,

        // Whether the next button is enabled or disabled
        enableNextButton: PropTypes.bool,

        // Call parent prevStep()
        onPrevStep: PropTypes.func,

        // Call parent onRestart()
        onRestart: PropTypes.func,

        // Call parent onDataChange(data)
        onDataChange: PropTypes.func,

        // Call parent onVehicleChange()
        onVehicleChange: PropTypes.func,

        // Call parent onCalculateUsageConsumption()
        onCalculateUsageConsumption: PropTypes.func
    };

    static defaultProps = {
        data: {},
        usageConsumptionData: undefined,
        vehicleDetail: {},
        enableNextButton: false
    };

    /**
     * Step components for this step
     *
     * @type [Component]
     */
    steps = [
        {
            component: Step2_1,
            validate: data => propertiesAreSet(data, ['rank2', 'rank3', 'fullcar', 'luggage'])
        },
        {
            component: Step2_2,
            validate: data => propertiesAreSet(data,  ['nbTripsPerDay', 'averageTripLength']) && data['averageTripLength'] > 0
        },
        {
            component: Step2_3,
            validate: data => {
                // Ensure data returns an integer
                const getData = dataKey => data[dataKey] ? data[dataKey] : 0;

                return (getData('mixCity') + getData('mixRoad') + getData('mixMotorway')) === 100
            }
        },
        {
            component: Step2_4,
            validate: data => propertiesAreSet(data,  ['drivingSpeed', 'drivingBehaviour'])
        },
        {
            component: UsageConsumptionResult
        }
    ];

    state = {
        // Current active step
        activeStep: 0
    };

    /**
     * Go to the previous available step.
     */
    onPrevStep = () => {
        const {activeStep} = this.state;

        if (activeStep === 0) {
            return this.props.onPrevStep();
        }

        this.setState({activeStep: activeStep - 1});
    };

    /**
     * Go to the next available step. If no more step are available
     * from steps list, call the onNextStep callback.
     */
    onNextStep = () => {
        this.setState({activeStep: this.state.activeStep + 1});
    };

    /**
     * Triggered when step data change.
     *
     * @param key
     *
     * @returns {Function}
     */
    onDataChange = key => value => {
        this.props.onDataChange && this.props.onDataChange(key, value);
    };

    /**
     * Returns whether all data in the current step are filled.
     *
     * @returns {boolean}
     */
    stepDataAreFilled() {
        const step = this.steps[this.state.activeStep];

        return step.validate ? step.validate(this.props.data) : true;
    }

    render() {
        const {
            className, 
            data, 
            usageConsumptionData,
            vehicleDetail, 
            enableNextButton,
            onRestart, 
            onCalculateUsageConsumption,
            onVehicleChange
        } = this.props;
        const {activeStep} = this.state;

        const classes = classNames(
            "cccu-step-usage-consumption",
            "row",
            "justify-content-center",
            {
                [className]: className !== undefined
            }
        );

        return (
            <div className={classes}>
                <div className="col-12 col-md-10">
                    <div className="row">
                        {activeStep < this.steps.length - 1 &&
                            <React.Fragment>
                                <div className="col-12">
                                    <StepTitle>{t('Step2_title')}</StepTitle>
                                </div>

                                <div className="col-12 steps-container">
                                    <NavResponsive active={activeStep} steps={[
                                        t('Step2_bar_need'),
                                        t('Step2_bar_trips'),
                                        t('Step2_bar_route'),
                                        t('Step2_bar_driving')
                                    ]}/>
                                </div>
                            </React.Fragment>
                        }

                        {this.steps.map((step, key) => {
                            if (activeStep !== key) {
                                return null;
                            }

                            const StepComponent  = step.component;

                            return React.cloneElement(
                                <ScrollOnMount>
                                    <StepComponent />
                                </ScrollOnMount>,
                                {
                                    className: "col-12",
                                    key,
                                    data,
                                    vehicleDetail,
                                    usageConsumptionData,
                                    onVehicleChange,
                                    onCalculateUsageConsumption,
                                    onDataChange: this.onDataChange
                                }
                            )
                        })}
                        <div className="col-12 container-actions">
                            <div className="row" style={{justifyContent:"center"}}>
                                {activeStep < this.steps.length - 1 ? (
                                    <React.Fragment>
                                        <div className="btn-actions-container col">
                                            <Button className="btn-previous btn-pre-nex-res-cccu" onClick={this.onPrevStep}>
                                                {t('CTA_Back')}
                                            </Button>
                                        </div>
                                        <div className="btn-actions-container col">
                                            <Button className="btn-next btn-pre-nex-res-cccu" disabled={!this.stepDataAreFilled()} onClick={this.onNextStep}>
                                                {t('CTA_Next')}
                                            </Button>
                                        </div>
                                    </React.Fragment>
                                ) : (
                                    <div className="btn-actions-container col" style={{maxWidth:613,minWidth:613,marginLeft:10,display:"inline-block"}} >
                                        <Button className="btn-restart btn-pre-nex-res-cccu" disabled={!enableNextButton} onClick={onRestart}>
                                            {t('CTA_Retry')}
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}

export default Step2;


// WEBPACK FOOTER //
// ./src/components/steps/Step2.js